import { useEffect, useMemo } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { SP, useDocumentSets } from "../../http/serviceportalApi";
import { useTranslations } from "../../hooks/useTranslations";
import { shouldShowUnreadCount, sort } from "../../selectors/documents";
import { getRemoteDataStatus, removeDuplicates } from "../../utils";
import ErrorText from "../ui/ErrorText";
import LoadingSpinner from "../ui/LoadingSpinner";
import DocumentsList, { DOCUMENTS_LIST_ID } from "./DocumentsList";
import styles from "./DocumentsTab.module.css";
import Nav from "./Nav";

interface Props {
  luid: SP.Luid;
  allLuids?: SP.Luid[];
  archive: SP.DocumentArchive;
  setSelectedOption?: (luid: SP.Luid) => void;
  showSets?: boolean;
  getAuthorizedLuids?: (set: SP.DocumentSetType) => SP.Luid[];
}

const DocumentsTab = ({
  luid,
  allLuids = [luid],
  archive,
  setSelectedOption,
  showSets = true,
  getAuthorizedLuids = () => allLuids,
}: Props) => {
  const { i18n } = useTranslations();
  const { data, isValidating, error, mutate } = useDocumentSets(allLuids);
  const allSets = useMemo(
    () =>
      data
        ? removeDuplicates(
            Object.values(data).flatMap((archive) =>
              Object.values(archive).flat(),
            ),
          )
        : [],
    [data],
  );
  const luidsWithSelectedArchive = useMemo(
    () =>
      data
        ? Object.entries(data).reduce((state: SP.Luid[], [luid, archives]) => {
            if (archives[archive]) {
              return [...state, +luid];
            }
            return state;
          }, [])
        : [],
    [data, archive],
  );
  const navSets = sort(data?.[luid]?.[archive] ?? [], archive);
  const status = getRemoteDataStatus({ isValidating, error });
  const firstNavSet = navSets[0];

  useEffect(() => {
    if (
      luidsWithSelectedArchive.length > 0 &&
      !luidsWithSelectedArchive.includes(luid)
    ) {
      luidsWithSelectedArchive[0] &&
        setSelectedOption?.(luidsWithSelectedArchive[0]);
    }
  }, [luid, luidsWithSelectedArchive, setSelectedOption]);

  return (
    <>
      {status === "success" && (
        <div className={styles.documents}>
          {showSets && (
            <Nav className={styles.nav}>
              {navSets.map(({ type, unreadCount, month }) => (
                <Nav.Item
                  key={type}
                  title={i18n.documentSet[type]}
                  to={`${type}${month ? `?datum=${month}` : ""}#${DOCUMENTS_LIST_ID}`}
                  unread={shouldShowUnreadCount(type) ? unreadCount : undefined}
                />
              ))}
            </Nav>
          )}

          <Routes>
            {allSets.map((set) => (
              <Route
                key={set.type}
                path={set.type}
                element={
                  <DocumentsList
                    key={set.type}
                    luid={luid}
                    archive={archive}
                    set={set}
                    getAuthorizedLuids={getAuthorizedLuids}
                    setUnreadCount={(unreadCount) => {
                      const archives = data?.[luid];
                      const sets = archives?.[archive];
                      if (!data || !archives || !sets) {
                        mutate();
                        return;
                      }
                      mutate(
                        {
                          ...data,
                          [luid]: {
                            ...archives,
                            [archive]: sets.map((s) =>
                              s.type === set.type ? { ...s, unreadCount } : s,
                            ),
                          },
                        },
                        { revalidate: false },
                      );
                    }}
                    onDownloadAllSuccess={() => mutate()}
                    setSelectedOption={setSelectedOption}
                  />
                }
              />
            ))}
            {firstNavSet && (
              <Route
                index
                element={<Navigate to={firstNavSet.type} replace={true} />}
              />
            )}
          </Routes>
        </div>
      )}

      {status === "validating" && (
        <div className={styles.status}>
          <LoadingSpinner />
        </div>
      )}

      {status === "failure" && (
        <div className={styles.status}>
          <ErrorText text={i18n.documents.loadError} />
        </div>
      )}
    </>
  );
};

export default DocumentsTab;
